import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const chartDataCache = new Map();
const chartDataRequests = new Map();
const chartDataListeners = new Map();

export const normalizeSignalChartInterval = (value) => {
  const interval = String(value || '').trim().toLowerCase();
  const numericIntervals = { 1: '1m', 2: '2m', 5: '5m', 15: '15m', 30: '30m', 60: '60m', 90: '90m' };
  if (Object.prototype.hasOwnProperty.call(numericIntervals, interval)) return numericIntervals[interval];
  if (interval === '60min') return '60m';
  if (interval === '1hr') return '1h';
  return interval;
};

const getChartDataKey = (scan) => JSON.stringify([
  scan?.id,
  String(scan?.period || '').trim(),
  normalizeSignalChartInterval(scan?.interval),
  scan?.include_extended_hours === true,
]);

const validateChartData = (chart, scan) => {
  const expectedInterval = normalizeSignalChartInterval(scan?.interval);
  const expectedPeriod = String(scan?.period || '').trim();
  const actualInterval = normalizeSignalChartInterval(chart?.interval);
  const actualPeriod = String(chart?.period || '').trim();
  const requestedPeriod = String(chart?.requested_period || '').trim();

  if (
    actualInterval !== expectedInterval
    || actualPeriod !== expectedPeriod
    || requestedPeriod !== expectedPeriod
    || (chart?.include_extended_hours === true) !== (scan?.include_extended_hours === true)
  ) {
    throw new Error(
      `Chart data mismatch (requested ${expectedPeriod}/${expectedInterval}, ${scan?.include_extended_hours === true ? 'all market hours' : 'regular market hours'}; received ${actualPeriod}/${actualInterval}, ${chart?.include_extended_hours === true ? 'all market hours' : 'regular market hours'}).`
    );
  }

  return chart;
};

export const getCachedSignalChartData = (scan) => chartDataCache.get(getChartDataKey(scan)) || null;

export const cacheSignalChartData = (scan, chart) => {
  if (!scan?.id) {
    throw new Error('A scan result is required to cache chart data.');
  }

  const validatedChart = validateChartData(chart, scan);
  const cacheKey = getChartDataKey(scan);
  chartDataCache.set(cacheKey, validatedChart);
  chartDataListeners.get(cacheKey)?.forEach((listener) => listener(validatedChart));
  return validatedChart;
};

export const subscribeSignalChartData = (scan, listener) => {
  const cacheKey = getChartDataKey(scan);
  const listeners = chartDataListeners.get(cacheKey) || new Set();
  listeners.add(listener);
  chartDataListeners.set(cacheKey, listeners);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) chartDataListeners.delete(cacheKey);
  };
};

export const getSignalChartData = async (scan, { forceRefresh = false } = {}) => {
  if (!scan?.id) {
    throw new Error('A scan result is required to load chart data.');
  }

  const cacheKey = getChartDataKey(scan);
  if (!forceRefresh && chartDataCache.has(cacheKey)) {
    return chartDataCache.get(cacheKey);
  }

  if (chartDataRequests.has(cacheKey)) {
    return chartDataRequests.get(cacheKey);
  }

  const token = localStorage.getItem('token');
  const request = axios.get(`${API}/scanner/signal-chart/${scan.id}`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { _ts: Date.now() },
  })
    .then((response) => {
      return cacheSignalChartData(scan, response.data || {});
    })
    .finally(() => {
      chartDataRequests.delete(cacheKey);
    });

  chartDataRequests.set(cacheKey, request);
  return request;
};
